import TagIcon from '@jetbrains/icons/tag.svg?react'
import Link from '@jetbrains/ring-ui-built/components/link/link'
import type {TagRenderProps} from '@jetbrains/ring-ui-built/components/tag/tag'
import Tag from '@jetbrains/ring-ui-built/components/tag/tag'
import {SvgIcon} from '@jetbrains/teamcity-ui/dist/components'
import classnames from 'classnames'
import type {ReactNode, SyntheticEvent} from 'react'
import * as React from 'react'

import {getBuildTypeHref, getPipelineHref} from '../../../routes'
import {getIsSakuraUI} from '../../../selectors'
import type {BuildId, BuildTypeId, PipelineId} from '../../../types'
import {resolveWebEntityLink, WebEntityType} from '../../../utils/entityLinks'
import {Modes} from '../../pages/BuildTypePage/BuildTypeOverviewTab/BuildTypeOverviewTab.modes'
import {removeBuildTag} from '../BuildActionsDropdown/TagDialog/TagDialog.actions'
import MiddleEllipsis from '../MiddleEllipsis/MiddleEllipsis'
import RouterLink from '../RouterLink/RouterLink'

import styles from './BuildTag.module.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  onSelect?: (() => unknown) | undefined
  className?: string
  buildTypeId?: BuildTypeId | undefined
  buildId?: BuildId | undefined
  label?: string | null | undefined
  multiple?: boolean
  selected?: boolean
  isPipelineStyle?: boolean
  href?: string
  removable?: boolean
  pipelineId?: PipelineId | null
}

const useHref = ({href, label, buildTypeId, pipelineId, selected = false}: Props) => {
  const baseHref = pipelineId
    ? getPipelineHref(pipelineId)
    : (resolveWebEntityLink(WebEntityType.BUILD_TYPE, buildTypeId) ?? '')

  if (href != null) {
    return href
  }

  if (label == null) {
    return '#'
  }

  const params = new URLSearchParams(location.search)
  params.set('tab', 'buildTypeHistoryList')

  if (selected) {
    params.delete('tag')
  } else {
    params.set('tag', label)
  }

  return `${baseHref}&${params.toString()}`
}

function BuildTag(props: Props) {
  const dispatch = useAppDispatch()
  const {
    className,
    label,
    multiple,
    selected = false,
    buildTypeId,
    onSelect,
    removable,
    buildId,
    pipelineId,
  } = props
  const stopPropagation = React.useCallback((e: SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const href = useHref(props)
  const classes = classnames(className, {
    [styles.selected]: selected,
    [styles.multiple]: multiple,
  })

  if (label == null) {
    return null
  }

  if (multiple === true) {
    return (
      <span className={classes} onClick={stopPropagation} role="presentation">
        <span>
          <SvgIcon icon={TagIcon} className={styles.icon} />
        </span>
        {label}
      </span>
    )
  }

  let render: ((props: TagRenderProps) => ReactNode) | undefined
  if (onSelect != null) {
    render = undefined
  } else if (isSakuraUI && (buildTypeId != null || pipelineId != null)) {
    render = ({children, ref, ...tagProps}) => (
      <RouterLink
        to={pipelineId ? getPipelineHref(pipelineId) : getBuildTypeHref(buildTypeId)}
        params={{
          tag: selected ? null : label,
          mode: Modes.BUILDS,
        }}
        withMergeParams
        {...tagProps}
        className={classnames(styles.link, tagProps.className)}
      >
        {children}
      </RouterLink>
    )
  } else {
    render = ({children, ref, ...tagProps}) => (
      <Link href={href} {...tagProps}>
        {children}
      </Link>
    )
  }

  return (
    <Tag
      containerClassName={classes}
      className={styles.tag}
      onClick={onSelect}
      onRemove={
        buildId != null ? () => dispatch(removeBuildTag(buildId, label, buildTypeId)) : undefined
      }
      readOnly={!removable || buildId == null}
      render={render}
    >
      <MiddleEllipsis tailLength={2}>{label}</MiddleEllipsis>
    </Tag>
  )
}

export default React.memo(BuildTag)
